// Lightweight markdown renderer — supports headings, bold/italic, lists, tables, paragraphs.
// Avoids extra npm deps.

function renderInline(text) {
  // escape
  let t = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  // bold
  t = t.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  // italic
  t = t.replace(/(^|[^*])\*([^*]+)\*/g, "$1<em>$2</em>");
  // inline code
  t = t.replace(/`([^`]+)`/g, "<code>$1</code>");
  return t;
}

function parseTable(lines, i) {
  // lines[i] and lines[i+1] must be table
  const header = lines[i].trim();
  const sep = lines[i + 1]?.trim() || "";
  if (!sep.match(/^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?$/)) return null;

  const split = (row) =>
    row.replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
  const headers = split(header);
  const rows = [];
  let j = i + 2;
  while (j < lines.length && lines[j].trim().startsWith("|")) {
    rows.push(split(lines[j].trim()));
    j++;
  }
  return { headers, rows, nextIndex: j };
}

export default function Markdown({ text }) {
  if (!text) return null;
  const lines = text.split(/\r?\n/);
  const out = [];
  let i = 0;
  let listBuf = null;
  const flushList = () => {
    if (listBuf) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc ml-6 my-2">
          {listBuf.map((li, k) => (
            <li key={k} dangerouslySetInnerHTML={{ __html: renderInline(li) }} />
          ))}
        </ul>
      );
      listBuf = null;
    }
  };
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      i++;
      continue;
    }
    if (trimmed.startsWith("|") && lines[i + 1]?.trim().startsWith("|")) {
      const tbl = parseTable(lines, i);
      if (tbl) {
        flushList();
        out.push(
          <table key={`t-${out.length}`}>
            <thead>
              <tr>
                {tbl.headers.map((h, k) => (
                  <th key={k} dangerouslySetInnerHTML={{ __html: renderInline(h) }} />
                ))}
              </tr>
            </thead>
            <tbody>
              {tbl.rows.map((r, k) => (
                <tr key={k}>
                  {r.map((c, kk) => (
                    <td key={kk} dangerouslySetInnerHTML={{ __html: renderInline(c) }} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        );
        i = tbl.nextIndex;
        continue;
      }
    }
    const h = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      flushList();
      const level = h[1].length;
      const content = renderInline(h[2]);
      const Tag = `h${Math.min(level, 6)}`;
      out.push(<Tag key={`h-${out.length}`} dangerouslySetInnerHTML={{ __html: content }} />);
      i++;
      continue;
    }
    if (/^[-*+]\s+/.test(trimmed)) {
      if (!listBuf) listBuf = [];
      listBuf.push(trimmed.replace(/^[-*+]\s+/, ""));
      i++;
      continue;
    }
    if (trimmed === "---") {
      flushList();
      out.push(<hr key={`hr-${out.length}`} />);
      i++;
      continue;
    }
    flushList();
    out.push(<p key={`p-${out.length}`} dangerouslySetInnerHTML={{ __html: renderInline(trimmed) }} />);
    i++;
  }
  flushList();
  return <div className="report-prose">{out}</div>;
}
